import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import type { Booking, Child, Event, SocialSecurityFund } from '../api/client'
import { createBooking, fetchBookings, fetchChildren, fetchSocialSecurityFunds, fetchUpcomingEvents } from '../api/client'
import { BookingInfoSection } from '../components/BookingInfoSection'
import { NavigationBar } from '../components/NavigationBar'
import { SocialSecurityFundSelect, SocialSecuritySubFundSelect } from '../components/SocialSecuritySelect'

export function AddBookingPage() {
  const navigate = useNavigate()
  const { t } = useTranslation(['bookings', 'common'])
  
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [friendError, setFriendError] = useState<string | null>(null)
  const [socialSecurityFundError, setSocialSecurityFundError] = useState<string | null>(null)
  const [children, setChildren] = useState<Child[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [socialSecurityFunds, setSocialSecurityFunds] = useState<SocialSecurityFund[]>([])
  
  // Form fields
  const [selectedChild, setSelectedChild] = useState<number | ''>('')
  const [selectedEvent, setSelectedEvent] = useState<number | ''>('')
  const [friendRegistrationNumbers, setFriendRegistrationNumbers] = useState<string[]>([])
  const [friendRegistrationInput, setFriendRegistrationInput] = useState<string>('')
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const [selectedSocialSecurityFund, setSelectedSocialSecurityFund] = useState<number | null>(null)
  const [selectedSocialSecuritySubFund, setSelectedSocialSecuritySubFund] = useState<number | null>(null)
  const [dypaProtocolNumber, setDypaProtocolNumber] = useState<string>('')
  const [dypaProtocolError, setDypaProtocolError] = useState<string | null>(null)
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      
      try {
        // Fetch children, events, bookings, and social security funds data in parallel
        const [childrenData, eventsData, bookingsData, fundsData] = await Promise.all([
          fetchChildren(),
          fetchUpcomingEvents(),
          fetchBookings(),
          fetchSocialSecurityFunds()
        ])
        
        setChildren(childrenData || [])
        setEvents(eventsData || [])
        setBookings(bookingsData || [])
        setSocialSecurityFunds(fundsData || [])
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'bookings' }))
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [t])
  
  // Reset sub-fund when fund changes
  useEffect(() => {
    setSelectedSocialSecuritySubFund(null)
  }, [selectedSocialSecurityFund])
  
  // Get the selected fund object
  const selectedFund = socialSecurityFunds.find(fund => fund.id === selectedSocialSecurityFund)
  
  // Get the sub-funds for the selected fund
  const subFunds = selectedFund?.sub_funds || []
  
  // Check if the selected fund has sub-funds
  const hasSocialSecuritySubFunds = selectedFund && selectedFund.sub_funds.length > 0
  
  // Filter events based on the selected child
  const filteredEvents = useMemo(() => {
    if (!selectedChild) return events;
    
    // Get the event IDs for which the selected child already has a booking
    const childBookingEventIds = bookings
      .filter(booking => booking.child === selectedChild && booking.status !== 'cancelled')
      .map(booking => booking.event);
    
    // Return only events for which the child doesn't have a booking
    return events.filter(event => !childBookingEventIds.includes(event.id));
  }, [events, selectedChild, bookings]);
  
  // Validate DYPA protocol number format: XX/YYYY/XXXXXXXXXXXX where YYYY must be the current year
  const validateDypaProtocolNumber = (value: string): { isValid: boolean; error: string | null } => {
    if (!value) return { isValid: true, error: null }; // Empty is allowed
    
    const pattern = /^(\d{2})\/(\d{4})\/(\d{12})$/;
    const match = value.match(pattern);
    
    if (!match) {
      return {
        isValid: false,
        error: t('errors.invalidDypaFormat', { 
          ns: 'bookings', 
          defaultValue: 'DYPA protocol number must be in format: XX/YYYY/XXXXXXXXXXXX' 
        })
      };
    }
    
    // Extract the year from the protocol number
    const year = parseInt(match[2], 10);
    const currentYear = new Date().getFullYear();
    
    if (year !== currentYear) {
      return {
        isValid: false,
        error: t('errors.invalidDypaYear', { 
          ns: 'bookings', 
          defaultValue: `DYPA protocol year must be ${currentYear}`,
          year: currentYear
        })
      };
    }
    
    return { isValid: true, error: null };
  };
  
  const handleDypaProtocolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDypaProtocolNumber(value);
    // Clear error when user types
    setDypaProtocolError(null);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!selectedChild || !selectedEvent) {
      setError(t('errors.requiredFields', { ns: 'bookings' }))
      return
    }
    
    // Validate terms acceptance
    if (!termsAccepted) {
      setError(t('errors.termsRequired', { ns: 'bookings' }))
      return
    }
    
    // Validate social security fund and sub-fund
    if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund) {
      // Don't set the top error message, as we're showing it under the field
      return
    }
    
    // Validate DYPA protocol number if ΔΥΠΑ is selected and value is provided
    if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && dypaProtocolNumber) {
      const { isValid, error } = validateDypaProtocolNumber(dypaProtocolNumber);
      if (!isValid) {
        setDypaProtocolError(error);
        return;
      }
    }
    
    setSubmitting(true)
    setError(null)
    setFriendError(null)
    setSocialSecurityFundError(null)
    
    try {
      // Set dypaProtocolNumber to empty string if the selected fund is not ΔΥΠΑ
      const finalDypaProtocolNumber = 
        selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' 
          ? dypaProtocolNumber 
          : '';
      
      // Create the base data object
      const baseData = {
        child: selectedChild,
        event: selectedEvent,
        friend_registration_numbers: friendRegistrationNumbers,
        social_security_fund_id: selectedSocialSecurityFund || undefined,
        dypa_protocol_number: finalDypaProtocolNumber,
        status: 'interested' as const
      };
      
      // Create the final data object with or without the sub-fund
      let bookingData;
      if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && selectedSocialSecuritySubFund) {
        // Include the sub-fund if it's selected and valid
        bookingData = {
          ...baseData,
          social_security_sub_fund_id: selectedSocialSecuritySubFund
        };
      } else {
        // Explicitly set sub-fund to undefined instead of null
        bookingData = {
          ...baseData,
          social_security_sub_fund_id: undefined
        };
      }
      
      await createBooking(bookingData)
      navigate('/') // Go back to home page after successful creation
    } catch (err: unknown) {
      // Check for specific error messages from the API
      const error = err as any; // Type assertion for backward compatibility
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for friend-related errors
        if (errorData.friend_registration_numbers) {
          const friendError = Array.isArray(errorData.friend_registration_numbers) 
            ? errorData.friend_registration_numbers[0] 
            : errorData.friend_registration_numbers;
          
          // Check for gender validation error
          if (friendError.includes('same gender')) {
            setFriendError(t('errors.friendGenderMismatch', { ns: 'bookings' }))
          } 
          // Check for invalid registration numbers
          else if (friendError.includes('Invalid registration numbers')) {
            setFriendError(t('errors.invalidRegistrationNumbers', { ns: 'bookings' }))
          }
          // Check for age difference error
          else if (friendError.includes('within 1.5 years of age difference')) {
            setFriendError(t('errors.ageDifference', { ns: 'bookings' }))
          }
          // Check for no booking error
          else if (friendError.includes('must have their own booking for the same period')) {
            setFriendError(t('errors.noBooking', { ns: 'bookings' }))
          }
          // Check for cancelled booking error
          else if (friendError.includes('must have an active booking')) {
            setFriendError(t('errors.cancelledBooking', { ns: 'bookings' }))
          }
          // Other friend-related errors
          else {
            setFriendError(friendError)
          }
        } 
        // Check for social security fund errors
        else if (errorData.social_security_fund_id) {
          const fundError = Array.isArray(errorData.social_security_fund_id)
            ? errorData.social_security_fund_id[0]
            : errorData.social_security_fund_id;
          
          // Check if it's the one booking per year error
          if (fundError.includes('one booking per year') || fundError.includes('non-private social security fund')) {
            setSocialSecurityFundError(t('errors.oneNonPrivateBookingPerYear', { ns: 'bookings' }));
          } else {
            setSocialSecurityFundError(fundError);
          }
        }
        // Check for social security fund errors from the model validation
        else if (errorData.social_security_fund) {
          const fundError = Array.isArray(errorData.social_security_fund)
            ? errorData.social_security_fund[0]
            : errorData.social_security_fund;
          
          // Check if it's the one booking per year error
          if (fundError.includes('one booking per year') || fundError.includes('non-private social security fund')) {
            setSocialSecurityFundError(t('errors.oneNonPrivateBookingPerYear', { ns: 'bookings' }));
          } else {
            setSocialSecurityFundError(fundError);
          }
        }
        else {
          setError(t('errors.createFailed', { ns: 'bookings' }))
        }
      } else {
        setError(t('errors.createFailed', { ns: 'bookings' }))
      }
    } finally {
      setSubmitting(false)
    }
  }
  
  // Validate registration number format: TR-{year}-{5 digits}
  const isValidRegistrationNumber = (regNumber: string): boolean => {
    const regNumberPattern = /^TR-\d{4}-\d{5}$/;
    return regNumberPattern.test(regNumber);
  }

  const addFriendRegistration = () => {
    if (!friendRegistrationInput.trim()) return
    
    // Check if already in the list
    if (friendRegistrationNumbers.includes(friendRegistrationInput.trim())) {
      setFriendError(t('errors.duplicateFriend', { ns: 'bookings' }))
      return
    }
    
    // Validate registration number format
    const regNumber = friendRegistrationInput.trim()
    if (!isValidRegistrationNumber(regNumber)) {
      setFriendError(t('errors.invalidRegistrationFormat', { ns: 'bookings' }))
      return
    }
    
    // Check if the registration number belongs to the selected child
    const selectedChildObj = children.find(child => child.id === selectedChild)
    if (selectedChildObj && selectedChildObj.registration_number === regNumber) {
      setFriendError(t('errors.cannotAddSelf', { ns: 'bookings', defaultValue: "You cannot add the child as their own friend" }))
      return
    }
    
    // Clear any friend errors when adding a new friend
    setFriendError(null)
    setFriendRegistrationNumbers([...friendRegistrationNumbers, regNumber])
    setFriendRegistrationInput('')
  }
  
  const removeFriendRegistration = (index: number) => {
    // Clear any friend errors when removing a friend
    setFriendError(null)
    
    const newList = [...friendRegistrationNumbers]
    newList.splice(index, 1)
    setFriendRegistrationNumbers(newList)
  }
  
  return (
    <div style={{ minHeight: '100vh' }}>
      <NavigationBar />
      
      {loading ? (
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        </div>
      ) : children.length === 0 ? (
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="card-neobrutalism p-8">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-neobrutalism-text mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="text-2xl font-black text-neobrutalism-text mb-4">
                {t('bookingsBox.noChildren.title', { ns: 'bookings' })}
              </h2>
              <p className="mb-6">
                {t('bookingsBox.noChildren.message', { ns: 'bookings' })}
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/" className="btn-neobrutalism bg-neobrutalism-red">
                  {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
                </Link>
                <Link to="/children/add" className="btn-neobrutalism bg-neobrutalism-green">
                  {t('bookingsBox.noChildren.addChildButton', { ns: 'bookings' })}
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <main className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="card-neobrutalism">
            <div className="flex items-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h1 className="text-2xl font-black text-neobrutalism-text">
                {t('bookingsBox.addBooking')}
              </h1>
            </div>
            
            {error && (
              <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
                <p className="text-neobrutalism-text font-bold">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Child Selection */}
              <div>
                <label htmlFor="child" className="block font-bold mb-2">
                  {t('bookingsBox.child')} *
                </label>
                <select
                  id="child"
                  value={selectedChild}
                  onChange={(e) => setSelectedChild(e.target.value ? parseInt(e.target.value, 10) : '')}
                  className="w-full p-2 border-2 border-black"
                  required
                >
                  <option value="">{t('bookingsBox.selectChild')}</option>
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {`${child.first_name} ${child.last_name}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Event Selection */}
              <div>
                <label htmlFor="event" className="block font-bold mb-2">
                  {t('bookingsBox.event')} *
                </label>
                <select
                  id="event"
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value ? parseInt(e.target.value, 10) : '')}
                  className="w-full p-2 border-2 border-black"
                  required
                >
                  <option value="">{t('bookingsBox.selectEvent')}</option>
                  {filteredEvents.map((event) => {
                    const startDate = new Date(event.start_date);
                    const endDate = new Date(event.end_date);
                    
                    // Format as dd/mm/yyyy
                    const formatDate = (date: Date) => {
                      const day = date.getDate().toString().padStart(2, '0');
                      const month = (date.getMonth() + 1).toString().padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    };
                    
                    // Get waiting list text based on current language
                    const waitingListText = t('waitingList', { ns: 'upcomingPeriods', defaultValue: 'waiting list' });
                    
                    return (
                      <option key={event.id} value={event.id}>
                        {event.name} ({formatDate(startDate)} - {formatDate(endDate)})
                        {event.is_full ? ` - ${waitingListText}` : ''}
                      </option>
                    );
                  })}
                </select>
                {selectedChild && events.length > 0 && filteredEvents.length === 0 && (
                  <p className="text-neobrutalism-red mt-1 font-bold">
                    {t('errors.noAvailableEvents', { ns: 'bookings', defaultValue: 'This child already has bookings for all available events' })}
                  </p>
                )}
              </div>
              
              {/* Social Security Fund Selection */}
              <div>
                <label htmlFor="social-security-fund" className="block font-bold mb-2">
                  {t('bookingsBox.socialSecurityRegistry')} *
                </label>
                <SocialSecurityFundSelect
                  funds={socialSecurityFunds}
                  value={selectedSocialSecurityFund}
                  onChange={(value) => {
                    setSelectedSocialSecurityFund(value);
                    setSocialSecurityFundError(null);
                  }}
                  placeholder={t('bookingsBox.selectSocialSecurityRegistry')}
                  className={`w-full ${socialSecurityFundError ? 'border-2 border-neobrutalism-red' : ''}`}
                />
                {socialSecurityFundError && (
                  <p className="text-neobrutalism-red mt-1 font-bold">
                    {socialSecurityFundError}
                  </p>
                )}
              </div>
              
              {/* Social Security Sub Fund Selection - Only show if the selected fund has sub-funds */}
              {selectedSocialSecurityFund && hasSocialSecuritySubFunds && (
                <div>
                  <label htmlFor="social-security-sub-fund" className="block font-bold mb-2">
                    {t('bookingsBox.socialSecurityFund')} {hasSocialSecuritySubFunds && '*'}
                  </label>
                  <SocialSecuritySubFundSelect
                    subFunds={subFunds}
                    value={selectedSocialSecuritySubFund}
                    onChange={setSelectedSocialSecuritySubFund}
                    placeholder={t('bookingsBox.selectSocialSecurityFund')}
                    className="w-full"
                    isDisabled={!selectedSocialSecurityFund || !hasSocialSecuritySubFunds}
                  />
                  {selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund && (
                    <p className="text-neobrutalism-red mt-1 font-bold">
                      {t('errors.socialSecurityFundRequired', { ns: 'bookings', defaultValue: 'Πρέπει να επιλέξετε Ταμείο Φορέα για αυτόν τον Φορέα Ασφάλισης' })}
                    </p>
                  )}
                </div>
              )}
              
              {/* DYPA Protocol Number */}
              {selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && (
                <div>
                  <label htmlFor="dypa-protocol-number" className="block font-bold mb-2">
                    {t('bookingsBox.dypaProtocolNumber')}
                  </label>
                  <input
                    type="text"
                    id="dypa-protocol-number"
                    value={dypaProtocolNumber}
                    onChange={handleDypaProtocolChange}
                    className={`w-full p-2 border-2 ${dypaProtocolError ? 'border-neobrutalism-red' : 'border-black'}`}
                    placeholder={`XX/${new Date().getFullYear()}/XXXXXXXXXXXX`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('bookingsBox.dypaProtocolFormat', { ns: 'bookings' })}: XX/{new Date().getFullYear()}/XXXXXXXXXXXX
                  </p>
                  {dypaProtocolError && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{dypaProtocolError}</p>
                  )}
                </div>
              )}
              
              {/* Friends */}
              <div>
                <label className="block font-bold mb-2">
                  {t('bookingsBox.friends')}
                </label>
                
                <div className="mt-2 flex">
                  <input
                    type="text"
                    value={friendRegistrationInput}
                    onChange={(e) => {
                      // Clear friend error when user types
                      if (friendError) setFriendError(null);
                      setFriendRegistrationInput(e.target.value);
                    }}
                    placeholder={t('bookingsBox.friendRegistrationPlaceholder')}
                    className={`w-full p-1 sm:p-2 text-xs sm:text-base border-2 ${friendError ? 'border-neobrutalism-red' : 'border-black'}`}
                  />
                  <button
                    type="button"
                    onClick={addFriendRegistration}
                    className="ml-2 btn-neobrutalism bg-neobrutalism-accent text-xs sm:text-base py-1 sm:py-2 px-2 sm:px-4"
                  >
                    {t('bookingsBox.add')}
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 mt-1">
                  {t('bookingsBox.registrationNumberFormat')}
                </p>
                
                {friendError && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{friendError}</p>
                )}
                
                {friendRegistrationNumbers.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {t('bookingsBox.addedFriends')}:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {friendRegistrationNumbers.map((regNum, index) => (
                        <div 
                          key={index} 
                          className="flex items-center bg-gray-100 rounded-full px-3 py-1 border border-gray-300"
                        >
                          <span className="text-sm font-medium mr-2">{regNum}</span>
                          <button
                            type="button"
                            onClick={() => removeFriendRegistration(index)}
                            className="text-gray-500 hover:text-neobrutalism-red focus:outline-none"
                            aria-label={t('bookingsBox.remove')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Fund-specific Information Section */}
              {selectedSocialSecurityFund && (
                <BookingInfoSection fundName={selectedFund?.name} />
              )}
              
              {/* Terms and Conditions */}
              <div className="flex items-start mt-4">
                <div className="flex items-center h-5">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-4 h-4 border-2 border-black"
                    required
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms" className="font-medium">
                    {t('bookingsBox.termsAgreement')} *
                  </label>
                  <p className="text-gray-600">
                    {t('bookingsBox.termsDescription')} <a href="https://ranch.gr/el/kanonismos" target="_blank" rel="noopener noreferrer" className="text-neobrutalism-accent underline">{t('bookingsBox.termsLink')}</a>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-row gap-4 pt-4">
                <Link to="/" className="btn-neobrutalism bg-neobrutalism-red w-1/2 text-center text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4">
                  {t('bookingsBox.cancel')}
                </Link>
                
                <button
                  type="submit"
                  disabled={submitting || !termsAccepted}
                  className={`btn-neobrutalism w-1/2 text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4 ${termsAccepted ? 'bg-neobrutalism-green' : 'bg-gray-400 cursor-not-allowed'}`}
                >
                  {submitting ? t('common.submitting') : t('bookingsBox.createBooking')}
                </button>
              </div>
            </form>
          </div>
        </main>
      )}
    </div>
  )
} 